import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { CategoryTabs } from "@/components/CategoryTabs";
import { api } from "@/lib/api";
import { Copy, Lock, Crown, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const CATS = [
  { id: "All",     label: "All" },
  { id: "Text",    label: "Text" },
  { id: "Image",   label: "Image" },
  { id: "Video",   label: "Video" },
  { id: "Music",   label: "Music" },
  { id: "Code",    label: "Code" },
  { id: "Agents",  label: "Agents" },
];

export default function PromptLibrary() {
  const [prompts, setPrompts] = useState([]);
  const [cat, setCat] = useState("All");
  const { user } = useAuth();
  const isPremium = user?.is_plus || user?.is_pro;

  useEffect(()=>{ api.get("/prompts").then(r => setPrompts(r.data)); }, []);

  const filtered = prompts.filter(p => cat === "All" || p.category === cat);

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="prompts-hero"
        variant="bare"
        eyebrow="Prompt Library"
        title="Professional Prompt Collection"
        sub="A curated library of high-performance prompts for serious users. Most of the collection is available to founding members only."
      />

      <CategoryTabs
        tabs={CATS}
        value={cat}
        onChange={setCat}
        testIdPrefix="prompt-cat"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="prompts-grid">
        {filtered.map(p => {
          const locked = p.is_pro && !isPremium;
          return (
            <div
              key={p.id}
              className="relative bg-white rounded-xl border border-[#E5E7EB] p-5"
              data-testid={`prompt-${p.id}`}
            >
              <div className="absolute top-4 right-4">
                {p.is_pro ? (
                  <span className="wf-pill bg-[#0B1220] text-white" data-testid={`prompt-${p.id}-lock`}>
                    <Lock size={10}/> Members only
                  </span>
                ) : (
                  <span className="wf-pill bg-[#F0FDF4] text-[#166534] border border-[#BBF7D0]" data-testid={`prompt-${p.id}-free`}>
                    Free
                  </span>
                )}
              </div>
              <div className="wf-label mb-2">{p.category} · {p.target_tool}</div>
              <h3 className="wf-h3 mb-2 tracking-tight">{p.title}</h3>
              {locked ? (
                <>
                  <p className="text-[13.5px] text-[#6B7280] leading-relaxed mb-4 line-clamp-2">
                    {p.prompt.slice(0, 140)}…
                  </p>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#F97316] hover:text-[#EA6A0A] transition-colors"
                    data-testid={`prompt-${p.id}-unlock`}
                  >
                    <Lock size={12}/> Unlock with Membership
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-[13px] text-[#0B1220] leading-relaxed font-mono bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg p-3 mb-3 whitespace-pre-wrap">{p.prompt}</p>
                  <div className="wf-meta mb-3"><strong className="text-[#0B1220] font-medium">Expected:</strong> {p.expected}</div>
                  <button
                    onClick={()=>navigator.clipboard.writeText(p.prompt)}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] hover:border-[#0B1220] text-[12.5px] font-medium text-[#0B1220] transition-colors"
                    data-testid={`copy-${p.id}`}
                  >
                    <Copy size={11}/> Copy prompt
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
